import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Paper,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { ensureInviteSocket, onModeratorInvite } from "../../../utils/stageInviteBus";
import { CheckCircle, Cancel, PlayArrow } from "@mui/icons-material";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { useAuth } from "../../../hooks";
import {
  useAcceptModeratorInvitation,
  useRejectModeratorInvitation,
  useCheckModeratorInviteStatus,
} from "../../../services/stagemode_api/moderator/moderator_api";
import { useErrorResponse } from "../../../hooks/useErrorResponse";
import { useNavigate, useParams } from "react-router-dom";

// Styled dark card section
const InteractionSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  borderRadius: "16px",
  backgroundColor: "#1A1A1A",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.6)",
  fontFamily: "Lexend, sans-serif",
  color: "#fff",
}));

// Styled action buttons
const ActionButton = styled(Button)(({ theme }) => ({
  minWidth: 120,
  height: 48,
  borderRadius: "24px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "14px",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.3)",
  },
}));

// Styled popup dialog
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    backgroundColor: "#1A1A1A",
    borderRadius: "16px",
    color: "#fff",
    fontFamily: "Lexend, sans-serif",
    minWidth: 400,
    maxWidth: 500,
  },
}));

// Types for moderator invitation
export type ModeratorInvitationStatus = "Pending" | "Accepted" | "Rejected";

export interface ModeratorInvitationProps {
  status?: ModeratorInvitationStatus;
  bookingId: string;
  inviteeName?: string;
}

const ModeratorInvitation: React.FC<ModeratorInvitationProps> = ({
  status = "Pending",
  bookingId,
  inviteeName = "You",
}) => {
  const [currentStatus, setCurrentStatus] =
    useState<ModeratorInvitationStatus>(status);
  const [isInvited, setIsInvited] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const socketSubscribedRef = useRef(false);

  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const { channel_name } = useParams();

  // Hooks for moderator invitation actions
  const {
    update: acceptModeratorInvitation,
    error: acceptModeratorInvitationError,
  } = useAcceptModeratorInvitation({
    key: ["accept-moderator-invitation"],
    params: {
      id: bookingId,
      userId: userInfo?.id ?? "",
    },
  });

  const {
    update: rejectModeratorInvitation,
    error: rejectModeratorInvitationError,
  } = useRejectModeratorInvitation({
    key: ["reject-moderator-invitation"],
    params: {
      id: bookingId,
      userId: userInfo?.id ?? "",
    },
  });

  const {
    update: checkModeratorInviteStatus,
    error: checkModeratorInviteStatusError,
  } = useCheckModeratorInviteStatus({
    key: ["check-moderator-invite-status"],
    params: {
      id: bookingId,
      userId: userInfo?.id ?? "",
    },
  });

  useErrorResponse(
    acceptModeratorInvitationError ||
      rejectModeratorInvitationError ||
      checkModeratorInviteStatusError
  );

  const handleAccept = async () => {
    try {
      await acceptModeratorInvitation(
        {},
        {
          onSuccess: () => {
            setCurrentStatus("Accepted");
            setShowPopup(false);
            setIsInvited(true);
          },
        }
      );
    } catch (error) {
      console.error("Failed to accept moderator invitation:", error);
    }
  };

  const handleReject = async () => {
    try {
      await rejectModeratorInvitation(
        {},
        {
          onSuccess: () => {
            setCurrentStatus("Rejected");
            setShowPopup(false);
            setIsInvited(true);
          },
        }
      );
    } catch (error) {
      console.error("Failed to reject moderator invitation:", error);
    }
  };

  const handleCheckInviteStatus = async () => {
    try {
      await checkModeratorInviteStatus(
        {},
        {
          onSuccess: (data) => {
            const st = data?.data?.status as ModeratorInvitationStatus;
            if (st) {
              setCurrentStatus(st);
              if (st === "Accepted" || st === "Rejected") {
                setIsInvited(true);
              }
            }
          },
        }
      );
    } catch (error) {
      console.error("Failed to check moderator invite status:", error);
    }
  };

  const handleStartModeration = () => {
    navigate(
      `/moderator/${channel_name}?bookingId=${bookingId}&userId=${userInfo?.id ?? ""}`
    );
  };

  // ---- FIXED SUBSCRIBE / CLEANUP ----
  // useEffect(() => {
  //   // Need both IDs to subscribe
  //   if (!bookingId || !userInfo?.id) return;

  //   // Avoid double subscriptions within this component lifecycle
  //   if (socketSubscribedRef.current) return;

  //   const socketHandler = SocketHandler.getInstance();

  //   // Correct signature: (bookingId, userId, callback)
  //   socketHandler.subscribeToModerator(
  //     bookingId,
  //     userInfo.id,
  //     (data: any) => {
  //       console.log("📩 ModeratorEvent Payload:", data);

  //       const type = data?.notification_type || data?.type;
  //       const role = (data?.role || "").toLowerCase();
  //       const targetUserId =
  //         data?.user_id ??
  //         data?.userId ??
  //         data?.user?.id ??
  //         data?.target_user_id;
  //       if (
  //         (type === "moderator-invite" || role === "moderator") &&
  //         String(targetUserId) === String(userInfo.id)
  //       ) {
  //         setShowPopup(true);
  //         setIsInvited(true);
  //         setCurrentStatus("Pending");
  //       }
  //     }
  //   );

  //   socketSubscribedRef.current = true;
  //   return () => {
  //     //Prefer the specific unsubscribe for moderator if available
  //     try {
  //       socketHandler.unsubscribeToModerator?.(bookingId, userInfo.id);
  //     } catch (e) {
  //       console.warn("unsubscribeFromModerator not available:", e);
  //     }
  //     socketSubscribedRef.current = false;
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [bookingId, userInfo?.id]);

useEffect(() => {
  const uid = userInfo?.id;
  if (!bookingId || !uid) return;

  ensureInviteSocket(bookingId, uid);

  const off = onModeratorInvite((data) => {
    setShowPopup(true);
    setIsInvited(true);
    setCurrentStatus("Pending");
  });

  return () => off();
}, [bookingId, userInfo?.id]);
  useEffect(() => {
    handleCheckInviteStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusChip = () => {
    const statusConfig: Record<
      ModeratorInvitationStatus,
      { label: string; color: string; backgroundColor: string }
    > = {
      Pending: {
        label: "Pending",
        color: "#FFA726",
        backgroundColor: "rgba(255, 167, 38, 0.1)",
      },
      Accepted: {
        label: "Accepted",
        color: "#66BB6A",
        backgroundColor: "rgba(102, 187, 106, 0.1)",
      },
      Rejected: {
        label: "Rejected",
        color: "#EF5350",
        backgroundColor: "rgba(239, 83, 80, 0.1)",
      },
    };

    const config = statusConfig[currentStatus];

    return (
      <Chip
        icon={<AdminPanelSettingsRoundedIcon />}
        label={config.label}
        sx={{
          color: config.color,
          backgroundColor: config.backgroundColor,
          border: `1px solid ${config.color}`,
          fontWeight: 600,
          fontSize: "12px",
        }}
      />
    );
  };

  const renderPendingActions = () => (
    <Stack direction="row" spacing={2} justifyContent="center">
      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        <ActionButton
          variant="contained"
          startIcon={<CheckCircle />}
          onClick={handleAccept}
          sx={{
            background: "linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)",
            color: "#fff",
            "&:hover": {
              background: "linear-gradient(135deg, #388E3C 0%, #4CAF50 100%)",
            },
          }}
        >
          Accept
        </ActionButton>
      </motion.div>

      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        <ActionButton
          variant="outlined"
          startIcon={<Cancel />}
          onClick={handleReject}
          sx={{
            borderColor: "#EF5350",
            color: "#EF5350",
            "&:hover": {
              borderColor: "#D32F2F",
              backgroundColor: "rgba(239, 83, 80, 0.1)",
            },
          }}
        >
          Reject
        </ActionButton>
      </motion.div>
    </Stack>
  );

  const renderAcceptedActions = () => (
    <Stack direction="row" spacing={2} justifyContent="center">
      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        <ActionButton
          variant="contained"
          startIcon={<PlayArrow />}
          onClick={handleStartModeration}
          sx={{
            background: "linear-gradient(135deg, #e50914 0%, #ff1744 100%)",
            color: "#fff",
            "&:hover": {
              background: "linear-gradient(135deg, #c62828 0%, #e50914 100%)",
            },
          }}
        >
          Open Moderator Panel
        </ActionButton>
      </motion.div>
    </Stack>
  );

  const renderRejectedState = () => (
    <Box textAlign="center">
      <Typography
        variant="body2"
        sx={{
          color: "#999",
          fontStyle: "italic",
          fontFamily: "Lexend, sans-serif",
        }}
      >
        Moderator invitation was declined
      </Typography>
    </Box>
  );

  return (
    <>
      {/* Invitation Popup */}
      <StyledDialog
        open={showPopup}
        onClose={() => setShowPopup(false)}
        aria-labelledby="invitation-dialog-title"
        aria-describedby="invitation-dialog-description"
      >
        <DialogTitle id="invitation-dialog-title">
          <Box display="flex" alignItems="center">
            <AdminPanelSettingsRoundedIcon sx={{ mr: 1, color: "#e50914" }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Lexend",
                fontWeight: 600,
                fontSize: "20px",
                color: "#fff",
              }}
            >
              Moderator Invitation
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "Lexend",
              color: "#fff",
              textAlign: "center",
              mb: 2,
            }}
          >
            You have been invited to be a moderator!
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#999",
              textAlign: "center",
              fontFamily: "Lexend",
            }}
          >
            Would you like to accept this moderator invitation?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 3, justifyContent: "center" }}>
          <Stack direction="row" spacing={2}>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <ActionButton
                variant="contained"
                startIcon={<CheckCircle />}
                onClick={handleAccept}
                sx={{
                  background:
                    "linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)",
                  color: "#fff",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #388E3C 0%, #4CAF50 100%)",
                  },
                }}
              >
                Accept
              </ActionButton>
            </motion.div>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <ActionButton
                variant="outlined"
                startIcon={<Cancel />}
                onClick={handleReject}
                sx={{
                  borderColor: "#EF5350",
                  color: "#EF5350",
                  "&:hover": {
                    borderColor: "#D32F2F",
                    backgroundColor: "rgba(239, 83, 80, 0.1)",
                  },
                }}
              >
                Reject
              </ActionButton>
            </motion.div>
          </Stack>
        </DialogActions>
      </StyledDialog>

      {/* Inline section after invite shows up */}
      {isInvited && (
        <Grid item xs={12}>
          <InteractionSection>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={3}
            >
              <Box display="flex" alignItems="center">
                <AdminPanelSettingsRoundedIcon sx={{ mr: 1, color: "#e50914" }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Lexend",
                    fontWeight: 600,
                    fontSize: "20px",
                    color: "#fff",
                  }}
                >
                  Moderator Invitation
                </Typography>
              </Box>
              {getStatusChip()}
            </Box>

            <Box mb={3}>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "Lexend",
                  color: "#fff",
                  textAlign: "center",
                  mb: 1,
                }}
              >
                {currentStatus === "Pending" &&
                  `${inviteeName} has been invited to be a moderator!`}
                {currentStatus === "Accepted" &&
                  `${inviteeName} is now a moderator for this session.`}
                {currentStatus === "Rejected" &&
                  `${inviteeName} declined the moderator invitation.`}
              </Typography>

              {currentStatus === "Pending" && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#999",
                    textAlign: "center",
                    fontFamily: "Lexend",
                  }}
                >
                  Please accept or reject this invitation to proceed
                </Typography>
              )}

              {currentStatus === "Accepted" && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#999",
                    textAlign: "center",
                    fontFamily: "Lexend",
                  }}
                >
                  Click “Open Moderator Panel” to start moderating.
                </Typography>
              )}
            </Box>

            {currentStatus === "Pending" && renderPendingActions()}
            {currentStatus === "Accepted" && renderAcceptedActions()}
            {currentStatus === "Rejected" && renderRejectedState()}
          </InteractionSection>
        </Grid>
      )}
    </>
  );
};

export default ModeratorInvitation;
